import { NavLink } from "react-router-dom";
import {
  Activity,
  Building2,
  CircleHelp,
  KeyRound,
  MailPlus,
  User,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import ThemeToggle from "./ThemeToggle";
import { cn } from "@/lib/utils";
import { useLanguage, useTranslation } from "@/i18n/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Injected at build time from package.json + git (see craco.config.js).
const APP_VERSION = process.env.REACT_APP_VERSION || "dev";
const APP_GIT_SHA = process.env.REACT_APP_GIT_SHA || "";
const APP_BUILD_TIME = process.env.REACT_APP_BUILD_TIME || "";

const nav = [
  { to: "/sensors", labelKey: "sidebar.nav.sensors", icon: Activity, testId: "nav-sensors" },
  { to: "/gyms", labelKey: "sidebar.nav.gyms", icon: Building2, testId: "nav-gyms" },
  { to: "/keys", labelKey: "sidebar.nav.apiKeys", icon: KeyRound, testId: "nav-api-keys" },
  { to: "/invites", labelKey: "sidebar.nav.invites", icon: MailPlus, testId: "nav-invites" },
  { to: "/members", labelKey: "sidebar.nav.members", icon: Users, testId: "nav-members" },
  { to: "/clients", labelKey: "sidebar.nav.clients", icon: User, testId: "nav-clients" },
  { to: "/help", labelKey: "sidebar.nav.help", icon: CircleHelp, testId: "nav-help" },
];

export default function Sidebar({ mobile = false, onNavigate, className }) {
  const t = useTranslation();
  const { language, languageOptions, setLanguage } = useLanguage();

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "fixed left-0 top-0 z-40 h-screen w-64 flex-col border-r border-border bg-chrome p-4",
        mobile && "static z-auto bg-chrome",
        className,
      )}
    >
      <div className="flex h-14 items-center gap-2 px-2">
        <Logo />
        <Badge
          variant="outline"
          className="h-4 rounded-lg border-primary/30 px-1.5 py-0 font-mono text-[9px] font-semibold uppercase leading-none text-primary"
        >
          Alpha
        </Badge>
      </div>
      <nav className="mt-6 flex flex-col gap-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onNavigate}
            data-testid={n.testId}
            className={({ isActive }) =>
              `group flex min-h-10 items-center gap-3 rounded-lg px-2 py-1.5 text-[13px] transition-colors ` +
              (isActive
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground")
            }
          >
            <n.icon className="h-4 w-4" strokeWidth={1.5} />
            <span>{t(n.labelKey)}</span>
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto space-y-3">
        <ThemeToggle />
        <div className="px-2">
          <div className="mono-eyebrow mb-2 !text-[9px]">{t("common.misc.language")}</div>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger
              className="h-9 rounded-lg border-border bg-background font-mono text-[11px]"
              data-testid="language-selector"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="start" className="border-border">
              {languageOptions.map((option) => (
                <SelectItem
                  key={option.code}
                  value={option.code}
                  className="font-mono text-xs"
                  data-testid={`language-option-${option.code}`}
                >
                  <span className="mr-2 uppercase text-muted-foreground">{option.shortLabel}</span>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div
          className="mono-eyebrow px-2 !text-[9px]"
          title={[APP_GIT_SHA && `commit ${APP_GIT_SHA}`, APP_BUILD_TIME && `built ${APP_BUILD_TIME}`]
            .filter(Boolean)
            .join(" · ") || undefined}
          data-testid="app-version"
        >
          {APP_VERSION}
        </div>
      </div>
    </aside>
  );
}
